import React from 'react';
import i18n from '../../../../../../i18n';
import { fmt } from '../../../../data/metrics';
import type { DriverBucket, DriverDimension } from '../../../../types';
import { drillRankColor } from '../charts/colors';
import { dimFmt, isMutedBucket, mutedBucketTitle, sourceColor } from './driverUtils';
import {
  baselineWords,
  bucketChangeTone,
  bucketShare,
  bucketWasWords,
  bucketWords,
  greyNote,
} from './driverWords';

/** Hover text with the exact figures behind the row's words. */
function rowTitle(
  dim: DriverDimension,
  b: DriverBucket,
  fmtVal: (n: number) => string,
  metricKey?: string,
  compareLabel?: string,
): string {
  const exact = dimFmt(dim, b.value, fmtVal);
  const words = bucketWords(dim, b, fmtVal, metricKey);
  const parts = [`${b.label}: ${words === exact ? words : `${words} (${exact})`}`];
  parts.push(
    i18n.t('performance:drillUi.reservationsCount', {
      count: b.sample,
      formatted: fmt.num(b.sample),
    }),
  );
  if (b.compareValue != null) {
    const was = bucketWasWords(dim, b, fmtVal, metricKey);
    const exactCmp = dimFmt(dim, b.compareValue, fmtVal);
    parts.push(
      `${compareLabel ?? i18n.t('performance:drillUi.vsCompare')}: ${
        was && !was.endsWith(exactCmp) ? `${was} (${exactCmp})` : exactCmp
      }`,
    );
  }
  const muted = mutedBucketTitle(b);
  if (muted) parts.push(muted);
  return parts.join(' · ');
}

/**
 * One dimension's buckets as rows a restaurateur can read: the group, a bar
 * scaled to the strongest group (with a dashed tick at the venue's own
 * average for a rate or an average), one figure in plain words ("13%",
 * "24%", "74 min") and the compare period's figure as "was 12%". Rows
 * the server reads as noise (thin or within variation) are greyed, and one
 * line under the rows says why.
 */
export function DriverBucketRows({
  dim,
  fmtVal,
  metricKey,
  compareLabel,
  size = 'sm',
}: {
  dim: DriverDimension;
  fmtVal: (v: number) => string;
  /** The metric the rows speak for: names the noun of "13% of …". */
  metricKey?: string;
  /** Compare-period name for the hover text, e.g. "3 Jun – 30 Jun". */
  compareLabel?: string;
  /** `lg` for a tile of its own, `sm` inside a correlates card. */
  size?: 'sm' | 'lg';
}) {
  const buckets = dim.buckets;
  if (!buckets.length) return null;
  const hasCompare = buckets.some(b => b.compareValue != null);
  const baseline =
    dim.mode !== 'share' && dim.baseline != null && dim.baseline > 0
      ? dim.baseline
      : null;
  // Bars draw the figure the row prints: a share of the dimension for a
  // share, the value itself for a rate or an average.
  const barValue = (b: DriverBucket) =>
    dim.mode === 'share' ? bucketShare(dim, b) : Math.abs(b.value);
  const max = Math.max(...buckets.map(barValue), baseline ?? 0, 0.0001);
  // Colour by strength, not by list position: lead time is listed by horizon.
  const rankOf = new Map(
    buckets
      .map((b, i) => ({ i, v: barValue(b) }))
      .sort((a, b) => b.v - a.v)
      .map((x, rank) => [x.i, rank] as const),
  );
  const topLabel = dim.topDriver?.label;
  // A zero draws nothing; anything else keeps a sliver so the row reads as present.
  const pctOf = (v: number) => (v === 0 ? 0 : Math.max(1.5, (v / max) * 100));
  const baseCaption = baselineWords(dim, fmtVal, metricKey);
  const grey = greyNote(dim);

  return (
    <div className={`dbr-wrap${size === 'lg' ? ' is-lg' : ''}`}>
      <div className={`dbr${hasCompare ? ' has-cmp' : ''}`} role="list">
        {buckets.map((b, i) => {
          const muted = isMutedBucket(b);
          const color = muted
            ? 'var(--line-2)'
            : dim.id === 'source'
              ? sourceColor(b.label)
              : drillRankColor(rankOf.get(i) ?? i, buckets.length);
          const was = hasCompare ? bucketWasWords(dim, b, fmtVal, metricKey) : null;
          const tone = hasCompare ? bucketChangeTone(dim, b) : null;
          return (
            <div
              className={`dbr-row${b.label === topLabel ? ' is-top' : ''}${muted ? ' is-muted' : ''}`}
              key={b.label}
              role="listitem"
              title={rowTitle(dim, b, fmtVal, metricKey, compareLabel)}
            >
              <span className="dbr-lab">{b.label}</span>
              <span className="dbr-track" aria-hidden>
                {baseline != null ? (
                  <span className="dbr-base" style={{ left: `${pctOf(baseline)}%` }} />
                ) : null}
                <span
                  className="dbr-fill"
                  style={{ width: `${pctOf(barValue(b))}%`, background: color }}
                />
              </span>
              <span className="dbr-val">{bucketWords(dim, b, fmtVal, metricKey)}</span>
              {hasCompare ? (
                <span className={`dbr-was${tone ? ` is-${tone}` : ''}`}>{was ?? ''}</span>
              ) : null}
            </div>
          );
        })}
      </div>
      {baseCaption ? (
        <p className="dbr-note dbr-note-base">
          <span className="dbr-note-tick" aria-hidden />
          {baseCaption}
        </p>
      ) : null}
      {grey ? <p className="dbr-note">{grey}</p> : null}
    </div>
  );
}
